import i18n from "@/i18n";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useEffect, useRef, useState } from "react";
import SpeechRecognition, {
    useSpeechRecognition,
} from "react-speech-recognition";
import useTauriSpeechErrorHandler from "./useTauriSpeechErrorHandler";

// react-speech-recognition's transcript is a single global shared by every
// useSpeechRecognition() consumer; several mic inputs are mounted at once, so we
// scope it to the input that started the dictation.
let activeMicOwnerId: number | null = null;
let micInstanceCounter = 0;

// SpeechRecognition.start/stopListening are marked /*#__PURE__*/ and get tree-shaken in prod: go through the manager.
const recognitionManager = () =>
    (
        SpeechRecognition as unknown as {
            getRecognitionManager: () => {
                startListening: (
                    options: Parameters<
                        typeof SpeechRecognition.startListening
                    >[0],
                ) => Promise<void>;
                stopListening: () => Promise<void>;
            };
        }
    ).getRecognitionManager();

const stopRecognition = () => recognitionManager().stopListening();

const useMicrophone = (
    setInputValue: (value: string) => void,
    maxInputLength: number,
) => {
    const { language } = i18n;

    const { sttManager } = useSdk();
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
    } = useSpeechRecognition();

    const [instanceId] = useState(() => {
        micInstanceCounter += 1;

        return micInstanceCounter;
    });

    const lastTranscriptRef = useRef("");
    const wasListeningRef = useRef(false);
    // Ref so startListenSession always reads the current locale without needing
    // to be recreated (and without re-registering the Tauri error listener).
    const languageRef = useRef(language);

    useEffect(() => {
        languageRef.current = language;
    }, [language]);

    // interimResults (needed by WKWebView/Tauri) is set to true by the library itself on init.
    const startListenSession = () =>
        recognitionManager().startListening({
            continuous: true,
            language: languageRef.current,
        });

    const { isDictationDisabled, resetDictationDisabled } =
        useTauriSpeechErrorHandler(startListenSession);

    const handleStartListening = () => {
        activeMicOwnerId = instanceId;
        resetTranscript();
        resetDictationDisabled();
        startListenSession();
    };

    // Only the input that started the dictation may stop the shared recognizer:
    // a stray stop (unmount, submit) from another input must not cut it off.
    const handleStopListening = () => {
        if (activeMicOwnerId !== instanceId) return;
        stopRecognition();
    };

    // Mise à jour en temps réel pendant la dictée
    useEffect(() => {
        if (activeMicOwnerId !== instanceId) return;
        if (!transcript) {
            // resetTranscript() a été appelé (soumission ou correction manuelle) :
            // on vide aussi lastTranscriptRef pour éviter qu'une correction STT
            // en cours ré-écrive le champ après sa réinitialisation.
            lastTranscriptRef.current = "";
            return;
        }
        if (transcript.length <= maxInputLength) {
            lastTranscriptRef.current = transcript;
            setInputValue(
                transcript.charAt(0).toUpperCase() + transcript.slice(1),
            );
        }
    }, [transcript, maxInputLength, setInputValue, instanceId]);

    // Correction STT via dictionnaire au moment où l'utilisateur arrête de parler
    useEffect(() => {
        const justStopped = !listening && wasListeningRef.current;

        if (
            justStopped &&
            activeMicOwnerId === instanceId &&
            lastTranscriptRef.current
        ) {
            const rawText = lastTranscriptRef.current;

            sttManager
                .correctText(rawText)
                .then((result) => {
                    // lastTranscriptRef is cleared when the transcript is reset (submit /
                    // manual clear): discard the correction if that happened in-flight.
                    if (
                        result.corrected &&
                        lastTranscriptRef.current &&
                        rawText.length <= maxInputLength
                    ) {
                        setInputValue(
                            result.corrected.charAt(0).toUpperCase() +
                                result.corrected.slice(1),
                        );
                    }
                })
                .catch(() => {
                    // fallback : on garde le transcript brut déjà affiché
                });
        }

        wasListeningRef.current = listening;
    }, [listening, sttManager, maxInputLength, setInputValue, instanceId]);

    useEffect(
        () => () => {
            if (activeMicOwnerId === instanceId) {
                stopRecognition();
                activeMicOwnerId = null;
            }
        },
        [instanceId],
    );

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Global cut, regardless of owner: a hidden tab must never
                // keep a ghost recording running.
                stopRecognition();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
        };
    }, []);

    return {
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        isDictationDisabled,
        listening,
        resetTranscript,
        handleStartListening,
        handleStopListening,
    };
};

export default useMicrophone;
